import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import { getApiUrl } from '../../config/api';
import { fetchCaptionsFromS3 } from '../VideoEditor/captionService';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import { useCaptionModal } from '../../context/CaptionModalContext';
import styles from './CaptionGenerationModal.module.css';
import loaderStyles from '../../assets/loader.module.css';

const CaptionGenerationModal: React.FC = () => {
  const { t } = useTranslation('videoEditor');
  const { isOpen, modalData, closeCaptionModal } = useCaptionModal();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  
  const [selectedLanguage, setSelectedLanguage] = useState('auto');
  const [isLoading, setIsLoading] = useState(false);
  const [existingCaptions, setExistingCaptions] = useState<any[]>([]);
  const [hasCheckedCaptions, setHasCheckedCaptions] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Language options - moved inside component to access t()
  const LANGUAGES = [
    { value: 'auto', label: `🌐 ${t('videoEditor.captionModal.languages.autoDetect')}` },
    { value: 'en', label: `🇺🇸 ${t('videoEditor.captionModal.languages.english')}` },
    { value: 'es', label: `🇪🇸 ${t('videoEditor.captionModal.languages.spanish')}` },
    { value: 'fr', label: `🇫🇷 ${t('videoEditor.captionModal.languages.french')}` },
    { value: 'de', label: `🇩🇪 ${t('videoEditor.captionModal.languages.german')}` },
    { value: 'it', label: `🇮🇹 ${t('videoEditor.captionModal.languages.italian')}` },
    { value: 'pt', label: `🇵🇹 ${t('videoEditor.captionModal.languages.portuguese')}` },
    { value: 'ru', label: `🇷🇺 ${t('videoEditor.captionModal.languages.russian')}` },
    { value: 'ja', label: `🇯🇵 ${t('videoEditor.captionModal.languages.japanese')}` },
    { value: 'ko', label: `🇰🇷 ${t('videoEditor.captionModal.languages.korean')}` },
    { value: 'zh', label: `🇨🇳 ${t('videoEditor.captionModal.languages.chinese')}` },
    { value: 'ar', label: `🇸🇦 ${t('videoEditor.captionModal.languages.arabic')}` },
    { value: 'hi', label: `🇮🇳 ${t('videoEditor.captionModal.languages.hindi')}` },
    { value: 'nl', label: `🇳🇱 ${t('videoEditor.captionModal.languages.dutch')}` },
    { value: 'sv', label: `🇸🇪 ${t('videoEditor.captionModal.languages.swedish')}` },
    { value: 'no', label: `🇳🇴 ${t('videoEditor.captionModal.languages.norwegian')}` },
    { value: 'da', label: `🇩🇰 ${t('videoEditor.captionModal.languages.danish')}` },
    { value: 'fi', label: `🇫🇮 ${t('videoEditor.captionModal.languages.finnish')}` },
  ];  
  // Check for existing captions when modal opens
  useEffect(() => {
    if (isOpen && modalData?.projectId && !hasCheckedCaptions) {
      checkExistingCaptions();
    }
  }, [isOpen, modalData, hasCheckedCaptions]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedLanguage('auto');
      setExistingCaptions([]);
      setHasCheckedCaptions(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  const checkExistingCaptions = async () => {
    if (!modalData?.projectId) return;
    
    try {
      const captions = await fetchCaptionsFromS3(modalData.projectId);
      setExistingCaptions(captions || []);
      setHasCheckedCaptions(true);
    } catch (error) {
      console.log('No existing captions found');
      setExistingCaptions([]);
      setHasCheckedCaptions(true);
    }
  };

  const handleGenerateCaptions = async () => {
    if (!modalData?.projectId || !modalData?.videoUrl) {
      addNotification('error', 'Missing project or video data');
      return;
    }

    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Construct instruction with language and description
      let instruction = '';
      if (selectedLanguage !== 'auto') {
        const languageLabel = LANGUAGES.find(lang => lang.value === selectedLanguage)?.label || '';
        instruction += `Generate captions in ${languageLabel.split(' ')[1] || selectedLanguage}. `;
      }
      
      const requestBody: any = {
        video: modalData.videoUrl,
        projectId: modalData.projectId
      };
      
      // Add instruction if provided
      if (instruction.trim()) {
        requestBody.instruction = instruction.trim();
      }
      
      const response = await fetch(getApiUrl('/video/generate-captions'), {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate captions');
      }
      
      const responseData = await response.json();
      
      addNotification('success', 'Captions generated successfully!');
      closeCaptionModal();
      
      // Navigate to caption editor with the video and project
      const queryParams = new URLSearchParams({
        'project-id': modalData.projectId,
        'video-url': modalData.videoUrl
      });
      navigate(`/caption?${queryParams.toString()}`);
      
    } catch (error) {
      console.error('Error generating captions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification('error', `Failed to generate captions! ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseExisting = () => {
    if (!modalData?.projectId || !modalData?.videoUrl) return;
    
    closeCaptionModal();
    
    // Navigate directly to caption editor
    const queryParams = new URLSearchParams({
      'project-id': modalData.projectId,
      'video-url': modalData.videoUrl
    });
    navigate(`/caption?${queryParams.toString()}`);
  };

  const handleClose = () => {
    closeCaptionModal();
  };

  if (!isOpen || !modalData) return null;
  const modalContent = (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {existingCaptions.length > 0 ? `🔄 ${t('videoEditor.captionModal.titleOptions')}` : `✨ ${t('videoEditor.captionModal.titleGenerate')}`}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Video Preview */}
          <div className={styles.videoContainer}>
            <VideoPlayer
              mediaItems={[]}
              video={modalData.videoUrl}
              captions={[]}
              videoRef={videoRef}
              onTimeUpdate={() => {}}
              onDurationChange={() => {}}
              mediaCurrentTime={0}
              setMediaCurrentTime={() => {}}
              onVideoPlay={() => {}}
              onVideoPause={() => {}}
            />
          </div>

          {/* Show existing captions info if found */}
          {existingCaptions.length > 0 && (
            <div className={styles.existingCaptionsInfo}>
              <div className={styles.infoIcon}>ℹ️</div>
              <div>
                <p className={styles.infoTitle}>{t('videoEditor.captionModal.existingFound')}</p>
                <p className={styles.infoText}>
                  {t('videoEditor.captionModal.existingMessage', { count: existingCaptions.length })}
                </p>
              </div>
            </div>
          )}

          {/* Language Selector */}
          <div className={styles.inputGroup}>
            <label className={styles.label}>{t('videoEditor.captionModal.language')}</label>
            <select 
              className={styles.select}
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              disabled={isLoading}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description Input */}
          {/* <div className={styles.inputGroup}>
            <label className={styles.label}>
              {t('videoEditor.captionModal.additionalInstructions')}
            </label>
            <textarea
              className={styles.textarea}
              placeholder={t('videoEditor.captionModal.instructionsPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={isLoading}
            />
          </div> */}
        </div>
        {/* Footer Actions */}
        <div className={styles.footer}>
          <button 
            className={styles.cancelButton}
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('videoEditor.captionModal.cancel')}
          </button>

          {existingCaptions.length > 0 && (
            <button 
              className={styles.useExistingButton}
              onClick={handleUseExisting}
              disabled={isLoading}
            >
              {t('videoEditor.captionModal.useExisting')}
            </button>
          )}

          <button 
            className={styles.generateButton}
            onClick={handleGenerateCaptions}
            disabled={isLoading || !modalData.videoUrl}
          >
            {isLoading ? (
              <span className={styles.loadingContainer}>
                <span className={loaderStyles["lds-roller-min"]}>
                  <div></div><div></div><div></div><div></div>
                  <div></div><div></div><div></div><div></div>
                </span>
                {t('videoEditor.captionModal.generating')}
              </span>
            ) : (
              existingCaptions.length > 0 ? t('videoEditor.captionModal.regenerate') : t('videoEditor.captionModal.generate')
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CaptionGenerationModal;